import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, RefreshCw, Check, X, Link2, Unlink, Sparkles } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { fmtEUR, fmtDate, EXPENSE } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export default function Detections() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [scanning, setScanning] = useState(false);
  const [drafts, setDrafts] = useState({});

  const { data: status } = useQuery({ queryKey: ["gmail-status"], queryFn: () => api.get("/gmail/status").then((r) => r.data) });
  const { data: detections = [] } = useQuery({ queryKey: ["detections"], queryFn: () => api.get("/detections").then((r) => r.data) });
  const { data: accounts = [] } = useQuery({ queryKey: ["accounts"], queryFn: () => api.get("/accounts").then((r) => r.data) });
  const { data: expenseCats = [] } = useQuery({ queryKey: ["categories", "expense"], queryFn: () => api.get("/categories?type=expense").then((r) => r.data) });

  useEffect(() => {
    const g = params.get("gmail");
    if (g === "ligado") { toast.success("Gmail ligado com sucesso"); setParams({}, { replace: true }); qc.invalidateQueries({ queryKey: ["gmail-status"] }); }
    if (g === "erro") { toast.error("Falha ao ligar o Gmail"); setParams({}, { replace: true }); }
  }, [params, setParams, qc]);

  const connect = async () => {
    try {
      const { data } = await api.get("/gmail/auth-url");
      window.location.href = data.url;
    } catch (err) { toast.error(apiError(err)); }
  };

  const disconnect = async () => {
    await api.post("/gmail/disconnect");
    toast.success("Gmail desligado");
    qc.invalidateQueries({ queryKey: ["gmail-status"] });
  };

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/gmail/scan?days=30");
      toast.success(`${data.faturas} faturas · ${data.despesas} despesas · ${data.excluded_empresa} empresa excluídas (${data.new} novos)`);
      qc.invalidateQueries({ queryKey: ["detections"] });
    } catch (err) { toast.error(apiError(err)); }
    finally { setScanning(false); }
  };

  const draft = (d) => drafts[d.id] || {
    amount: d.amount ?? "", category: d.category || "", due_date: d.due_date || "",
    account_id: accounts.find((a) => a.type !== "cash")?.id || accounts[0]?.id || "",
  };
  const setDraft = (id, patch) => setDrafts((p) => ({ ...p, [id]: { ...draft({ id, ...p[id] }), ...p[id], ...patch } }));

  const confirm = async (d) => {
    const dr = draft(d);
    try {
      await api.put(`/detections/${d.id}`, {
        amount: dr.amount === "" ? null : parseFloat(String(dr.amount).replace(",", ".")),
        category: dr.category || null,
        due_date: dr.due_date || null,
      });
      const q = d.kind === "despesa" && dr.account_id ? { account_id: dr.account_id } : {};
      await api.post(`/detections/${d.id}/confirm`, null, { params: q });
      toast.success(d.kind === "fatura" ? "Fatura adicionada" : "Despesa registada (por confirmar no extrato)");
      qc.invalidateQueries();
    } catch (err) { toast.error(apiError(err)); }
  };

  const dismiss = async (d) => {
    await api.post(`/detections/${d.id}/dismiss`);
    toast.success("Ignorado");
    qc.invalidateQueries({ queryKey: ["detections"] });
  };

  return (
    <div className="space-y-5">
      <h1 className="font-heading text-2xl font-bold">Deteções por email</h1>

      {/* Cartão Gmail */}
      <div className="bg-card border border-border rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-secondary flex items-center justify-center">
            <Mail className="text-primary" size={20} />
          </div>
          <div>
            <div className="font-medium flex items-center gap-2">
              Gmail
              {status?.connected
                ? <Badge className="bg-[#2E7D5C] text-white">ligado</Badge>
                : <Badge variant="outline">não ligado</Badge>}
            </div>
            <div className="text-xs text-muted-foreground">
              {status?.connected ? `${status.email}${status.last_scan ? ` · última análise ${fmtDate(status.last_scan.slice(0, 10))}` : ""}` : "Só leitura — deteta faturas e recibos, nunca envia nem apaga."}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          {status?.connected ? (
            <>
              <Button onClick={scan} disabled={scanning} className="rounded-full" data-testid="gmail-scan-btn">
                <RefreshCw size={16} className={`mr-1 ${scanning ? "animate-spin" : ""}`} /> {scanning ? "A analisar…" : "Analisar emails"}
              </Button>
              <Button variant="outline" onClick={disconnect} className="rounded-full" data-testid="gmail-disconnect-btn">
                <Unlink size={16} className="mr-1" /> Desligar
              </Button>
            </>
          ) : (
            <Button onClick={connect} className="rounded-full" data-testid="gmail-connect-btn">
              <Link2 size={16} className="mr-1" /> Ligar Gmail
            </Button>
          )}
        </div>
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Sparkles size={14} /> A IA separa promoções, notificações de transportadora e emails da empresa. Reveja antes de confirmar.
      </p>

      {/* Lista de deteções */}
      <div className="space-y-3" data-testid="detections-list">
        {detections.length === 0 ? (
          <p className="text-sm text-muted-foreground py-8 text-center bg-card border border-border rounded-lg">
            Sem deteções por rever. {status?.connected ? "Clique em \"Analisar emails\"." : "Ligue o Gmail para começar."}
          </p>
        ) : (
          detections.map((d) => {
            const dr = draft(d);
            return (
              <div key={d.id} className="bg-card border border-border rounded-lg p-4" data-testid={`detection-${d.id}`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.kind === "fatura"
                        ? <Badge className="bg-[#C75D42] text-white">fatura a pagar</Badge>
                        : <Badge className="bg-primary text-white">despesa</Badge>}
                      <span className="font-medium truncate">{d.merchant || d.subject}</span>
                      {d.confidence != null && <span className="text-xs text-muted-foreground">{Math.round(d.confidence * 100)}%</span>}
                    </div>
                    <div className="text-xs text-muted-foreground truncate mt-0.5">{d.from}</div>
                    {d.reason && <div className="text-xs text-muted-foreground mt-1 italic">{d.reason}</div>}
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <Label className="text-xs">Valor (€)</Label>
                    <Input value={dr.amount} onChange={(e) => setDraft(d.id, { amount: e.target.value })} className="h-9" data-testid={`det-amount-${d.id}`} />
                  </div>
                  <div className="md:col-span-1">
                    <Label className="text-xs">Categoria</Label>
                    <Select value={dr.category} onValueChange={(v) => setDraft(d.id, { category: v })}>
                      <SelectTrigger className="h-9" data-testid={`det-category-${d.id}`}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        {expenseCats.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {d.kind === "fatura" ? (
                    <div>
                      <Label className="text-xs">Vencimento</Label>
                      <Input type="date" value={dr.due_date} onChange={(e) => setDraft(d.id, { due_date: e.target.value })} className="h-9" data-testid={`det-due-${d.id}`} />
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs">Conta</Label>
                      <Select value={dr.account_id} onValueChange={(v) => setDraft(d.id, { account_id: v })}>
                        <SelectTrigger className="h-9" data-testid={`det-account-${d.id}`}><SelectValue placeholder="Conta" /></SelectTrigger>
                        <SelectContent>
                          {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <Button onClick={() => confirm(d)} className="rounded-full flex-1" data-testid={`det-confirm-${d.id}`}>
                      <Check size={16} className="mr-1" /> Confirmar
                    </Button>
                    <Button variant="outline" size="icon" onClick={() => dismiss(d)} className="rounded-full" data-testid={`det-dismiss-${d.id}`}>
                      <X size={16} />
                    </Button>
                  </div>
                </div>

                {(d.entity || d.reference) && (
                  <div className="text-xs text-muted-foreground mt-2">
                    {d.entity && `Entidade ${d.entity}`} {d.reference && `· Referência ${d.reference}`}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
